/**
 * GC64 · LIVE Intake→Media-Job Bridge verification section.
 * =========================================================
 *
 * Renders inside the existing Admin Preflight page
 * (`/admin/preflight/c2`), reusing the session cookie the founder
 * is already carrying. No password handling, no DevTools, no
 * curl. Admin-only via the parent route's `RoleRoute` — this
 * component adds no new auth surface.
 *
 * On click, it runs three EXISTING admin endpoints in sequence:
 *
 *   1. GET  /api/admin/preflight/uploads_history          (deploy check)
 *   2. POST /api/admin/preflight/media_path/reconcile?limit=50
 *   3. GET  /api/admin/preflight/media_path/{evidence_id} (per-file trace)
 *
 * and displays only the eight founder-authored verdict fields.
 * No second diagnostic implementation is created — the component
 * is purely a driver over the existing endpoints.
 *
 * The IMG_5114.mov identification is a case-insensitive
 * substring match against the `filename` field the reconcile
 * outcome now carries (see `media_controller/intake_bridge.py`
 * · Feb 13, 2026 · GC64 UI hint). If the founder wants a
 * different file traced, they pass the target filename via the
 * `targetFilename` prop below — defaults to `"img_5114"`.
 */
import { useCallback, useState } from "react";
import axios from "axios";
import { Loader2, PlayCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// External-processor mapping — GC64 · MP-5 (Feb 2026).
//
// PRIOR behaviour: infer only from `job.status`, which conflated
// four distinct handoff outcomes into a single "NOT_REACHED"
// label whenever the job was pre-terminal. The founder's LIVE
// trace could not tell whether:
//   (a) the external POST never fired,
//   (b) the POST fired but was rejected,
//   (c) the processor accepted and we're waiting on the callback,
//   (d) or the processor completed.
//
// The backend now emits `external_dispatch_verdict` per job — a
// closed-set aggregate over the derivatives' dispatch state. We
// consume that directly here so the UI tells the truth.
//
// Closed-set values (must match `_external_dispatch_verdict` in
// backend/routes/preflight_c2.py):
//
//   "PASS"                        — job.status = ready_for_review
//   "FAIL"                        — job.status ∈ {failed, cancelled}
//   "ACCEPTED_AWAITING_CALLBACK"  — processor accepted; awaiting callback
//   "DISPATCH_ATTEMPTED"          — outbound POST claimed but no
//                                    processor_job_id yet (POST in
//                                    flight, or the task was GC'd)
//   "DISPATCH_FAILED"             — outbound POST or processor run
//                                    failed for every attempted deriv
//   "NOT_ATTEMPTED"               — no derivative claimed; nothing
//                                    left the NDG boundary
function deriveExternalProcessor(job) {
  if (!job) return { verdict: "NOT_REACHED", status: null };
  const backendVerdict = job.external_dispatch_verdict;
  if (typeof backendVerdict === "string" && backendVerdict.length > 0) {
    return { verdict: backendVerdict, status: job.status || "" };
  }
  // Legacy fallback for older backends that don't emit the field.
  const st = job.status || "";
  if (st === "ready_for_review") return { verdict: "PASS", status: st };
  if (st === "failed" || st === "cancelled")
    return { verdict: "FAIL", status: st };
  return { verdict: "NOT_REACHED", status: st };
}

function countBy(list, key) {
  const c = {};
  for (const o of list || []) c[o[key]] = (c[o[key]] || 0) + 1;
  return c;
}

function findTargetOutcome(outcomes, targetFilename) {
  const needle = (targetFilename || "").toLowerCase();
  if (!needle) return null;
  return (
    (outcomes || []).find(
      (o) => ((o.filename || "").toLowerCase()).includes(needle)
    ) || null
  );
}

export default function GC64LiveVerification({
  targetFilename = "img_5114",
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [state, setState] = useState(null); // {deploy, reconcile, targetEvidenceId, mediaPath, target}

  const run = useCallback(async () => {
    setBusy(true);
    setErr(null);
    setState(null);
    try {
      // Step 1 · deploy check
      const dh = await axios.get(
        `${API}/api/admin/preflight/uploads_history`,
        { withCredentials: true }
      );

      // Step 2 · reconcile legacy intake evidences (idempotent)
      const rec = await axios.post(
        `${API}/api/admin/preflight/media_path/reconcile?limit=50`,
        null,
        { withCredentials: true }
      );

      // Step 3 · locate the target evidence (defaults to IMG_5114.mov).
      // First look at the reconcile outcomes (has filename field);
      // fall back to the deploy-check `latest_evidence_docs` block.
      const outcomes = rec?.data?.outcomes || [];
      let target = findTargetOutcome(outcomes, targetFilename);
      if (!target) {
        const recent = dh?.data?.latest_evidence_docs || [];
        const match = recent.find(
          (e) =>
            ((e.filename || "").toLowerCase()).includes(
              (targetFilename || "").toLowerCase()
            )
        );
        if (match)
          target = {
            evidence_id: match.evidence_id,
            filename: match.filename,
            status: "prior_run",
            job_id: null,
          };
      }
      const evId = target?.evidence_id || null;

      // Step 4 · per-file media-path trace with bounded polling.
      // The reconcile endpoint returns as soon as the media_jobs
      // row is created + confirm_upload has flipped it to UPLOADED,
      // but the derivative-dispatch to the external processor is
      // scheduled as an asyncio.create_task (fire-and-forget).
      // Without polling here, the UI would render a snapshot BEFORE
      // the external dispatch takes visible effect — which is what
      // caused the founder-reported "two-press race" on LIVE. Poll
      // the SAME canonical endpoint until either (a) the linked
      // job appears with an FK-linkage entry, OR (b) a bounded
      // budget elapses. Never masks the underlying worker-pickup
      // failure — a truly stalled job STILL renders through, with
      // its actual status in the FINAL_STATUS tile.
      let mediaPath = null;
      if (evId) {
        const url = `${API}/api/admin/preflight/media_path/${evId}`;
        const budgetMs = 12000;   // total budget: 12s
        const stepMs = 1500;      // per-poll wait
        const startedAt = Date.now();
        // Immediate first read.
        let mp = await axios.get(url, { withCredentials: true });
        mediaPath = mp.data;
        // Retry until FK linkage is present OR budget elapses.
        while (Date.now() - startedAt < budgetMs) {
          const jobs = mediaPath?.media_jobs_for_owner_within_24h || [];
          const linked = jobs.find((j) => j.linkage === "intake_evidence_id");
          // Exit early if we have a linked job (any status — even
          // transient — is a truthful signal that the reconcile
          // is durable and the render is stable).
          if (linked) break;
          await new Promise((r) => setTimeout(r, stepMs));
          mp = await axios.get(url, { withCredentials: true });
          mediaPath = mp.data;
        }
      }

      setState({
        deploy: dh.data,
        reconcile: rec.data,
        target,
        mediaPath,
      });
    } catch (e) {
      const detail =
        e?.response?.data?.detail || e?.message || "GC64 verification failed";
      setErr(String(detail));
    } finally {
      setBusy(false);
    }
  }, [targetFilename]);

  // Derived, closed-set fields for display.
  const view = (() => {
    if (!state) return null;
    const d = state.deploy || {};
    const r = state.reconcile || {};
    const t = state.target;
    const mp = state.mediaPath;
    // Pick the intake-linked job for the target evidence, if any.
    const jobList = mp?.media_jobs_for_owner_within_24h || [];
    const linkedJob =
      jobList.find((j) => j.linkage === "intake_evidence_id") ||
      jobList[0] ||
      null;
    const ext = deriveExternalProcessor(linkedJob);
    const finalStatus = linkedJob?.status || "not_dispatched";
    // Per-derivative dispatch state so the founder can see exactly
    // which of probe / preview_mp4 / thumbnail reached which stage.
    const derivStates = linkedJob?.derivative_dispatch_states || null;
    const derivSummary = derivStates
      ? ["probe", "preview_mp4", "thumbnail"]
          .map((n) => `${n}:${(derivStates[n]?.verdict || "absent")}`)
          .join(" · ")
      : "unavailable";
    // GC64 · MP-6 (Feb 2026) — surface the bounded `last_error_kind`
    // string on every derivative that failed dispatch, so the LIVE
    // trace pinpoints the actual boundary category (network / timeout
    // / http_4xx / http_5xx / misconfigured / r2_misconfigured /
    // disabled) without needing shell access to the LIVE deployment.
    // `last_error` is truncated at render time — we never leak
    // secrets, only the bounded error class + short message.
    const derivErrors = derivStates
      ? ["probe", "preview_mp4", "thumbnail"]
          .map((n) => {
            const d = derivStates[n] || {};
            if (!d.last_error_kind && !d.last_error) return null;
            const kind = d.last_error_kind || "unknown";
            const msg = (d.last_error || "").slice(0, 120);
            return `${n}[${kind}]${msg ? ": " + msg : ""}`;
          })
          .filter(Boolean)
          .join(" · ") || "none"
      : "unavailable";
    const failureBoundary =
      mp?.boundary_verdict ||
      (t
        ? "EVIDENCE_LOCATED_BUT_NO_MEDIA_PATH_DATA"
        : `TARGET_FILENAME_NOT_FOUND(${targetFilename})`);

    return {
      LIVE_SOURCE_PIN: d.intake_bridge_source_pin || "MISSING",
      LIVE_UI_S3_HOOK: d.ui_s3_finalize_hook_pin || "MISSING",
      LIVE_INTAKE_MEDIA_BRIDGE_ENABLED: !!d.intake_media_bridge_enabled,
      RECONCILE_RESULT: r?.counts || countBy(r?.outcomes, "status"),
      IMG_5114_MEDIA_JOB: linkedJob?.id || t?.job_id || "NONE",
      EXTERNAL_PROCESSOR: ext.verdict,
      DERIVATIVE_STATES: derivSummary,
      DERIVATIVE_ERRORS: derivErrors,
      FINAL_STATUS: finalStatus,
      FAILURE_BOUNDARY: failureBoundary,
    };
  })();

  return (
    <div
      data-testid="gc64-verification-section"
      className="mt-8 rounded-2xl border-2 border-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-700 p-4"
    >
      <p className="text-xs font-bold uppercase tracking-widest text-indigo-700 dark:text-indigo-300">
        GC64 · Intake→Media-Job bridge
      </p>
      <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 mt-1">
        LIVE verification
      </h2>
      <p className="text-sm text-slate-600 dark:text-slate-300 mt-2">
        Runs three read-only admin endpoints against this deployment
        using your current session, reconciles any legacy
        <code className="mx-1 px-1 rounded bg-slate-200 dark:bg-slate-800">
          ui_s3_multi_camera_intake
        </code>
        uploads that never acquired a media job, and traces
        <code className="mx-1 px-1 rounded bg-slate-200 dark:bg-slate-800">
          {targetFilename}
        </code>
        through the external processor. Reuses existing endpoints — no
        second diagnostic path.
      </p>

      <button
        data-testid="gc64-run"
        onClick={run}
        disabled={busy}
        className="mt-4 inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-bold min-h-[44px]"
      >
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <PlayCircle className="w-4 h-4" />
        )}
        Run GC64 LIVE verification
      </button>

      {err && (
        <div
          data-testid="gc64-error"
          className="mt-4 p-3 rounded-xl border-2 border-rose-300 bg-rose-50 dark:bg-rose-900/20 dark:border-rose-700 text-sm text-rose-900 dark:text-rose-100 break-words"
        >
          {err}
        </div>
      )}

      {view && (
        <dl
          data-testid="gc64-verdict"
          className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm"
        >
          {[
            ["LIVE_SOURCE_PIN", view.LIVE_SOURCE_PIN],
            ["LIVE_UI_S3_HOOK", view.LIVE_UI_S3_HOOK],
            [
              "LIVE_INTAKE_MEDIA_BRIDGE_ENABLED",
              String(view.LIVE_INTAKE_MEDIA_BRIDGE_ENABLED).toUpperCase(),
            ],
            [
              "RECONCILE_RESULT",
              typeof view.RECONCILE_RESULT === "object"
                ? Object.entries(view.RECONCILE_RESULT)
                    .map(([k, v]) => `${k}:${v}`)
                    .join(" · ") || "empty"
                : String(view.RECONCILE_RESULT),
            ],
            ["IMG_5114_MEDIA_JOB", view.IMG_5114_MEDIA_JOB],
            ["EXTERNAL_PROCESSOR", view.EXTERNAL_PROCESSOR],
            ["DERIVATIVE_STATES", view.DERIVATIVE_STATES],
            ["DERIVATIVE_ERRORS", view.DERIVATIVE_ERRORS],
            ["FINAL_STATUS", view.FINAL_STATUS],
            ["FAILURE_BOUNDARY", view.FAILURE_BOUNDARY],
          ].map(([label, value]) => (
            <div
              key={label}
              data-testid={`gc64-field-${label
                .toLowerCase()
                .replace(/_/g, "-")}`}
              className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3"
            >
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                {label}
              </p>
              <p className="mt-1 font-mono text-sm text-slate-900 dark:text-slate-100 break-words">
                {String(value)}
              </p>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
